import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  DatePicker,
  Flex,
  Modal,
  Select,
  Typography
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  AuditLogArchiveFormatEnum,
  organizationsAuditArchivesCreateMutation,
  organizationsAuditArchivesListQueryKey,
  type SeverityEnum
} from 'src/api/main'
import { translateAuditAction, translateAuditSeverity } from 'src/utils'

const { RangePicker } = DatePicker
const { Text } = Typography

const RequiredMark = () => (
  <span
    aria-hidden="true"
    style={{ color: 'var(--ant-color-error)', marginInlineStart: 2 }}
  >
    *
  </span>
)

type AuditExportModalProps = {
  open: boolean
  onClose: () => void
  organizationId: number
  defaultPeriod: [Dayjs | null, Dayjs | null] | null
  filters: Record<string, string>
  /** When set, the export is scoped to this application (sent as body.application). */
  applicationId?: number
  /** Optional path to the Exports list — if set, the success toast gets a link. */
  viewExportsPath?: string
}

export const AuditExportModal = ({
  open,
  onClose,
  organizationId,
  defaultPeriod,
  filters,
  applicationId,
  viewExportsPath
}: AuditExportModalProps) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [format, setFormat] = useState<AuditLogArchiveFormatEnum>(
    AuditLogArchiveFormatEnum.CSV
  )
  const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null] | null>(
    () =>
      defaultPeriod ?? [
        dayjs().subtract(30, 'day').startOf('day'),
        dayjs().endOf('day')
      ]
  )

  const createMutation = useMutation({
    ...organizationsAuditArchivesCreateMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationsAuditArchivesListQueryKey({
          path: { organization_id: organizationId }
        })
      })
      if (viewExportsPath) {
        message.success({
          content: (
            <span>
              {t('workspace.auditPage.exportModal.queued')}{' '}
              <a
                onClick={() => {
                  message.destroy()
                  navigate(viewExportsPath)
                }}
              >
                {t('workspace.auditPage.exportModal.viewExports')}
              </a>
            </span>
          ),
          duration: 6
        })
      } else {
        message.success(t('workspace.auditPage.exportModal.queued'))
      }
      onClose()
    },
    onError: () => message.error(t('workspace.auditPage.exportModal.failed'))
  })

  const periodStart = period?.[0] ?? null
  const periodEnd = period?.[1] ?? null
  const canSubmit =
    periodStart != null && periodEnd != null && !createMutation.isPending

  // App-scope exports need `application` in TWO places:
  //   • `body.application` — indexed column used for fast frontend filtering.
  //   • `filters.application_id` — the actual selector the worker reads to pick
  //     which log rows to include when generating the archive.
  const effectiveFilters = applicationId
    ? { ...filters, application_id: String(applicationId) }
    : filters

  const handleSubmit = () => {
    if (!periodStart || !periodEnd) return

    createMutation.mutate({
      path: { organization_id: organizationId },
      body: {
        period_start: periodStart.startOf('day').toISOString(),
        period_end: periodEnd.endOf('day').toISOString(),
        format,
        filters: effectiveFilters,
        ...(applicationId ? { application: applicationId } : {})
      }
    })
  }

  const renderFilterValue = (key: string, value: string) => {
    if (key === 'severity')
      return translateAuditSeverity(t, value as SeverityEnum)
    if (key === 'action') return translateAuditAction(t, value)

    return value
  }

  return (
    <Modal
      open={open}
      title={t('workspace.auditPage.exportModal.title')}
      onCancel={() => {
        if (!createMutation.isPending) onClose()
      }}
      onOk={handleSubmit}
      okText={t('workspace.auditPage.exportModal.submit')}
      cancelText={t('workspace.auditPage.exportModal.cancel')}
      okButtonProps={{
        disabled: !canSubmit,
        loading: createMutation.isPending
      }}
      destroyOnHidden
    >
      <Flex vertical gap={16}>
        <Text type="secondary">
          {t('workspace.auditPage.exportModal.description')}
        </Text>

        <Flex vertical gap={6}>
          <Text style={{ fontSize: 12, fontWeight: 500 }}>
            {t('workspace.auditPage.exportModal.periodLabel')}
            <RequiredMark />
          </Text>
          <RangePicker
            value={period}
            onChange={(next) =>
              setPeriod(next as [Dayjs | null, Dayjs | null] | null)
            }
            style={{ width: '100%' }}
          />
        </Flex>

        <Flex vertical gap={6}>
          <Text style={{ fontSize: 12, fontWeight: 500 }}>
            {t('workspace.auditPage.exportModal.formatLabel')}
            <RequiredMark />
          </Text>
          <Select<AuditLogArchiveFormatEnum>
            value={format}
            onChange={setFormat}
            style={{ width: '100%' }}
            options={[
              {
                value: AuditLogArchiveFormatEnum.CSV,
                label: t('workspace.auditPage.exportModal.formatCsv')
              },
              {
                value: AuditLogArchiveFormatEnum.XLSX,
                label: t('workspace.auditPage.exportModal.formatXlsx')
              },
              {
                value: AuditLogArchiveFormatEnum.JSONL,
                label: t('workspace.auditPage.exportModal.formatJsonl')
              }
            ]}
          />
        </Flex>

        <Flex vertical gap={6}>
          <Text style={{ fontSize: 12, fontWeight: 500 }}>
            {t('workspace.auditPage.exportModal.filtersLabel')}
          </Text>
          {Object.keys(effectiveFilters).length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.auditPage.exportModal.filtersNone')}
            </Text>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {Object.entries(effectiveFilters).map(([key, value]) => (
                <li key={key}>
                  <Text type="secondary">
                    {t(`workspace.auditPage.exportModal.filters.${key}`, {
                      defaultValue: key
                    })}
                    :
                  </Text>{' '}
                  <Text>{renderFilterValue(key, value)}</Text>
                </li>
              ))}
            </ul>
          )}
        </Flex>
      </Flex>
    </Modal>
  )
}
